import React, { FC, memo } from 'react';
import { animated } from 'react-spring';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import cn from 'classnames';
import Portal from 'components/Portal';
import css from './styles.module.scss';
import NotificationsContent from './NotificationsContent';
import { IFormattedNotification } from './types';
import { usePanel } from './hooks/usePanel';

interface IPanelProps {
  open: boolean;
  anchorRef: React.RefObject<HTMLDivElement>;
  onClose: () => void;
  buttonRef: React.RefObject<HTMLDivElement>;
  notificationsList: IFormattedNotification[];
  markAsRead: (id: number) => void;
  markAsReadAndRemove: (id: number) => void;
  markAllAsRead: () => void;
  handleGetTask: (task_id: number) => void;
}

const Panel: FC<IPanelProps> = ({
  open,
  anchorRef,
  onClose,
  buttonRef,
  notificationsList,
  markAsRead,
  markAsReadAndRemove,
  markAllAsRead,
  handleGetTask,
}) => {
  const {
    t,
    panelRef,
    position,
    animation,
    filteredNotifications,
    selectedTab,
    setSelectedTab,
  } = usePanel({
    anchorRef,
    open,
    onClose,
    buttonRef,
    notificationsList,
  });

  if (!open || !position) return null;

  return (
    <Portal inBody>
      <animated.div
        ref={panelRef}
        className={cn(css.notification__panel, {
          _isOpen: open,
        })}
        style={{
          ...animation,
          top: position.top + 8,
          right: position.right,
        }}
      >
        <div className={css.notification__header}>
          <strong>{t(`notifications_title`)}</strong>
          {(!!filteredNotifications.length ||
            notificationsList.filter((notification) => !notification.is_read)
              .length > 0) && (
            <button type="button" onClick={markAllAsRead}>
              {t(`markAllAsRead`)}
            </button>
          )}
        </div>

        <Tabs
          selectedIndex={selectedTab}
          onSelect={(index) => setSelectedTab(index)}
          className={cn(css.tabs, 'react-tabs')}
        >
          <TabList className={cn(css.tablist, 'react-tabs__tab-list')}>
            <Tab>{t('all')}</Tab>
            <Tab>{t('unread')}</Tab>
          </TabList>

          <TabPanel className={cn(css.tabPanel, 'react-tabs__tab-panel')}>
            <NotificationsContent
              notifications={filteredNotifications}
              markAsRead={markAsRead}
              emptyText={t('noNewNotifications')}
              markAsReadAndRemove={markAsReadAndRemove}
              handleGetTask={handleGetTask}
            />
          </TabPanel>
          <TabPanel className={cn(css.tabPanel, 'react-tabs__tab-panel')}>
            <NotificationsContent
              notifications={filteredNotifications.filter(
                (notification) => !notification.is_read,
              )}
              markAsRead={markAsRead}
              emptyText={t('noNewNotifications')}
              markAsReadAndRemove={markAsReadAndRemove}
              handleGetTask={handleGetTask}
            />
          </TabPanel>
        </Tabs>
      </animated.div>
    </Portal>
  );
};

export default memo(Panel);
